'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  Users,
  CheckCircle,
  Clock,
  Lightbulb,
  MessageSquare,
  Star,
  TrendingUp,
  Activity,
  SkipForward,
  Target,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { authService } from '@/lib/authService';

interface PerTourStats {
  count: number;
  completed: number;
  in_progress: number;
  not_started: number;
  avg_time: number;
  total_interactions: number;
  completion_rate: number;
}

interface StepStats {
  step_id: string;
  flow_id: string | null;
  completions: number;
  skips: number;
  hints_shown: number;
  hints_dismissed: number;
  avg_time_seconds: number;
}

interface FlowStats {
  flow_id: string;
  starts: number;
  completions: number;
  completion_rate: number;
}

interface EngagementDataPoint {
  date: string;
  events: number;
  completions: number;
  starts: number;
}

interface TourAnalytics {
  total_users: number;
  completed: number;
  in_progress: number;
  not_started: number;
  avg_time_spent: number;
  total_interactions: number;
  total_hints_shown: number;
  total_hints_dismissed: number;
  hint_dismissal_rate: number;
  total_feedback: number;
  avg_feedback_rating: number;
  event_counts: Record<string, number>;
  per_tour: Record<string, PerTourStats>;
  step_stats: StepStats[];
  flow_stats: FlowStats[];
  engagement_timeline: EngagementDataPoint[];
}

const STATUS_COLORS = ['#10B981', '#F59E0B', '#EF4444'];
const CHART_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4'];

function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

function formatTourKey(key: string): string {
  return key
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function TourAnalyticsDashboard() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<TourAnalytics | null>(null);
  const [selectedTourKey, setSelectedTourKey] = useState<string | null>(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      const url = selectedTourKey
        ? `/api/v1/training/tour-analytics?tour_key=${encodeURIComponent(selectedTourKey)}`
        : '/api/v1/training/tour-analytics';
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('[TourAnalyticsDashboard] Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedTourKey]);

  useEffect(() => {
    void loadAnalytics();
  }, [loadAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading tour analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics || analytics.total_users === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
        <BarChart3 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          No Tour Analytics Yet
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Tour completion analytics will appear here once users start engaging with guided tours.
        </p>
      </div>
    );
  }

  const tourKeys = Object.keys(analytics.per_tour);
  const statusData = [
    { name: 'Completed', value: analytics.completed, color: STATUS_COLORS[0] },
    { name: 'In Progress', value: analytics.in_progress, color: STATUS_COLORS[1] },
    { name: 'Not Started', value: analytics.not_started, color: STATUS_COLORS[2] },
  ].filter((d) => d.value > 0);

  const perTourChartData = tourKeys.map((key) => ({
    name: formatTourKey(key),
    completed: analytics.per_tour[key].completed,
    in_progress: analytics.per_tour[key].in_progress,
    not_started: analytics.per_tour[key].not_started,
  }));

  const topSteps = [...analytics.step_stats]
    .sort((a, b) => b.completions - a.completions)
    .slice(0, 10);

  const flowChartData = analytics.flow_stats
    .slice(0, 10)
    .map((f) => ({
      name: f.flow_id,
      starts: f.starts,
      completions: f.completions,
    }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-indigo-600" />
            Tour Completion Analytics
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Step-level engagement, completion rates, and AI hint effectiveness across guided tours
          </p>
        </div>
        <select
          value={selectedTourKey ?? ''}
          onChange={(e) => setSelectedTourKey(e.target.value || null)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">All Tours</option>
          {tourKeys.map((key) => (
            <option key={key} value={key}>{formatTourKey(key)}</option>
          ))}
        </select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-300 font-medium">Total Learners</p>
              <p className="text-3xl font-bold text-blue-900 dark:text-white mt-2">{analytics.total_users}</p>
            </div>
            <Users className="w-12 h-12 text-blue-300 dark:text-blue-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 dark:text-green-300 font-medium">Completion Rate</p>
              <p className="text-3xl font-bold text-green-900 dark:text-white mt-2">
                {analytics.total_users > 0 ? Math.round((analytics.completed / analytics.total_users) * 100) : 0}%
              </p>
              <p className="text-xs text-green-700 dark:text-green-200 mt-1">
                {analytics.completed} of {analytics.total_users} completed
              </p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-300 dark:text-green-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900 dark:to-orange-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-600 dark:text-amber-300 font-medium">Avg Time Spent</p>
              <p className="text-3xl font-bold text-amber-900 dark:text-white mt-2">
                {formatTime(analytics.avg_time_spent)}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-200 mt-1">
                {analytics.total_interactions} total interactions
              </p>
            </div>
            <Clock className="w-12 h-12 text-amber-300 dark:text-amber-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-900 dark:to-purple-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-violet-600 dark:text-violet-300 font-medium">AI Hints Shown</p>
              <p className="text-3xl font-bold text-violet-900 dark:text-white mt-2">{analytics.total_hints_shown}</p>
              <p className="text-xs text-violet-700 dark:text-violet-200 mt-1">
                {analytics.hint_dismissal_rate}% dismissal rate
              </p>
            </div>
            <Lightbulb className="w-12 h-12 text-violet-300 dark:text-violet-600" />
          </div>
        </div>
      </div>

      {/* Status Distribution + Per-Tour Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {statusData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" />
              Status Distribution
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }: { name?: string; value?: number }) => `${name ?? ''}: ${value ?? 0}`}
                >
                  {statusData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {perTourChartData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              Per-Tour Breakdown
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={perTourChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB',
                  }}
                />
                <Legend />
                <Bar dataKey="completed" stackId="a" fill="#10B981" name="Completed" />
                <Bar dataKey="in_progress" stackId="a" fill="#F59E0B" name="In Progress" />
                <Bar dataKey="not_started" stackId="a" fill="#EF4444" name="Not Started" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Engagement Timeline */}
      {analytics.engagement_timeline.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-indigo-600" />
            Engagement Timeline (Last 30 Days)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={analytics.engagement_timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                stroke="#6B7280"
                tickFormatter={(d: string) => d.slice(5)}
              />
              <YAxis stroke="#6B7280" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="events" stroke="#3B82F6" name="Total Events" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="starts" stroke="#F59E0B" name="Flow Starts" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="completions" stroke="#10B981" name="Completions" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Flow Performance */}
      {flowChartData.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            Flow Performance
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={flowChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#6B7280" angle={-15} textAnchor="end" height={60} />
              <YAxis stroke="#6B7280" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                }}
              />
              <Legend />
              <Bar dataKey="starts" fill="#3B82F6" name="Started" radius={[4, 4, 0, 0]} />
              <Bar dataKey="completions" fill="#10B981" name="Completed" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Step-Level Analytics Table */}
      {topSteps.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <SkipForward className="w-5 h-5 text-indigo-600" />
              Step-Level Analytics (Top 10)
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Completion counts, skip rates, and hint usage per step
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Step ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Flow
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Completions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Skips
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Skip Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Hints Shown
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Avg Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {topSteps.map((step) => {
                  const total = step.completions + step.skips;
                  const skipRate = total > 0 ? Math.round((step.skips / total) * 100) : 0;
                  return (
                    <tr key={`${step.flow_id ?? 'none'}:${step.step_id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-white">
                        {step.step_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {step.flow_id ?? '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-gray-700 dark:text-gray-300">{step.completions}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <SkipForward className="w-4 h-4 text-orange-500" />
                          <span className="text-gray-700 dark:text-gray-300">{step.skips}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 w-16">
                            <div
                              className={`h-2 rounded-full ${
                                skipRate >= 50 ? 'bg-red-500' :
                                skipRate >= 25 ? 'bg-orange-500' :
                                'bg-green-500'
                              }`}
                              style={{ width: `${skipRate}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{skipRate}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-violet-500" />
                          <span className="text-gray-700 dark:text-gray-300">{step.hints_shown}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 dark:text-gray-300">
                        {step.avg_time_seconds > 0 ? formatTime(step.avg_time_seconds) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Event Summary + Feedback */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.keys(analytics.event_counts).length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              Event Summary
            </h3>
            <div className="space-y-2">
              {Object.entries(analytics.event_counts)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {analytics.total_feedback > 0 && (
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900 dark:to-orange-900 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-600" />
              Feedback Summary
            </h3>
            <div className="flex items-center gap-6">
              <div>
                <p className="text-4xl font-bold text-amber-900 dark:text-amber-100">
                  {analytics.avg_feedback_rating.toFixed(1)}
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-200 mt-1">
                  Average rating from {analytics.total_feedback} response{analytics.total_feedback === 1 ? '' : 's'}
                </p>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-8 h-8 ${
                        star <= Math.round(analytics.avg_feedback_rating)
                          ? 'text-amber-500 fill-amber-500'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
